import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import MomentUtils from '@date-io/moment';
import InputMask from 'react-input-mask';
import {
  DatePicker as MUIDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { withStyles } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MUITextField from '@material-ui/core/TextField';
import moment from 'moment';
import classnames from 'classnames';
import InputAdornment from '@material-ui/core/InputAdornment';

import SavingTextField from './SavingTextField';
import Icon from './Icon';
import { color } from '../../app/theme';
import chevronDownIcon from '../../assets/images/svg/ic-chevron-down.svg';
import chevronUpIcon from '../../assets/images/svg/ic-chevron-up.svg';

const useStyles = theme => ({
  root: {
    position: 'relative',
  },
  Datepicker: {
    display: 'flex',
    borderRadius: '22px',
    padding: '11px 16px 10px 24px',
    ...theme.input.default.wrapper,
    color: color.$13,
    backgroundColor: `${color.$7}`,
    '& input': {
      height: '21px',
      fontSize: '15px',
      padding: 0,
      '&::placeholder': {
        color: color.$10,
        opacity: 1,
      },
    },
    '& .MuiInputBase-root': {
      '&:before, &:after': {
        display: 'none',
      },
    },
  },
  focus: {
    border: `1px solid ${color.$1}`,
  },
  Modal: {
    position: 'absolute',
    marginTop: '4px',
    zIndex: 999,
  },
});

function getMaxDate() {
  const now = new Date();
  return new Date(now.getFullYear() + 10, now.getMonth());
}

class DatePicker extends Component {
  constructor(props) {
    super(props);
    this.inputRef = createRef();
    this.state = {
      isOpen: false,
      isOnYearChange: false,
      dateString: props.selectedDate,
    };
  }

  componentDidMount() {
    const { selectedDate } = this.props;
    this.setState({
      dateString: selectedDate || '',
    });
  }

  componentDidUpdate(prevProps) {
    const { selectedDate } = this.props;
    const { isOnYearChange } = this.state;
    if (prevProps.selectedDate !== selectedDate) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        dateString: selectedDate
          ? moment(selectedDate).format('YYYY-MM-DD')
          : '',
      });
    }

    if (isOnYearChange) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        isOnYearChange: false,
        isOpen: true,
      });
    }
  }

  onFocus = () => {
    this.setState({ isOpen: true });
  };

  onCalendarClick = () => {
    this.setState(state => ({ isOpen: !state.isOpen }));
  };

  onBlur = e => {
    const dateString = e.target.value;
    const { selectedDate } = this.props;

    if (
      !/\d{4}-\d{2}-\d{2}/.test(dateString) ||
      dateString !== moment(selectedDate).format('YYYY-MM-DD')
    ) {
      this.setState({
        dateString: selectedDate
          ? moment(selectedDate).format('YYYY-MM-DD')
          : '',
      });
    }
  };

  onYearChange = () => {
    this.setState({ isOnYearChange: true });
  };

  onChangeDate = date => {
    const { onChange } = this.props;
    this.setState({
      dateString: moment(date).format('YYYY-MM-DD'),
    });

    onChange(date);
  };

  onAccept = date => {
    this.setState({
      dateString: date.format('YYYY-MM-DD'),
      isOpen: false,
    });
  };

  onChange = e => {
    const dateString = e.target.value;
    const { minDate, maxDate, onChange } = this.props;
    const date = moment(dateString, 'YYYY-MM-DD');
    this.setState({ dateString });

    if (
      date.isValid() &&
      date.isSameOrAfter(minDate || new Date('1900-01-01'), 'day') &&
      date.isSameOrBefore(maxDate || getMaxDate(), 'day') &&
      /\d{4}-\d{2}-\d{2}/.test(dateString)
    ) {
      onChange(date);
    }
  };

  onClickAway = () => {
    this.setState({ isOpen: false });
  };

  onKeyDown = e => {
    if (e.keyCode === 13) {
      this.setState({
        isOpen: false,
      });
      this.inputRef.current.blur();
    }
  };

  renderTextField(type) {
    const {
      classes,
      placeholder,
      style,
      disabled,
      isHideDropdownIcon,
      onMouseDown,
      isError,
    } = this.props;
    const { isOpen, dateString } = this.state;
    switch (type) {
      case 'isSavingText':
        return (
          <SavingTextField
            style={style}
            placeholder={placeholder}
            InputProps={{ onFocus: this.onFocus, onBlur: this.onBlur }}
            type="text"
            value={dateString}
            onChange={this.onChange}
          />
        );
      case 'isCalendar':
        return (
          <Icon
            type="calendar"
            onClick={this.onCalendarClick}
            calendarOpen={isOpen}
          />
        );
      default:
        return (
          <InputMask
            mask="9999-99-99"
            autoComplete="off"
            maskChar={null}
            onFocus={this.onFocus}
            value={
              /\d{4}-\d{2}-\d{2}/.test(dateString)
                ? dateString
                : dateString instanceof moment
                ? moment(dateString).format('YYYY-MM-DD')
                : dateString
            }
            disabled={disabled}
            onBlur={this.onBlur}
            onChange={this.onChange}
            onMouseDown={() => {
              if (!disabled) {
                this.setState({ isOpen: !isOpen });
                if (onMouseDown) {
                  onMouseDown();
                }
              }
            }}
            onKeyDown={this.onKeyDown}
          >
            {inputProps => (
              <MUITextField
                inputRef={this.inputRef}
                type="text"
                className={classnames(classes.Datepicker, {
                  [classes.focus]: isOpen,
                  [classes.isError]: isError,
                })}
                disabled={disabled}
                placeholder={placeholder}
                InputProps={{
                  endAdornment: isHideDropdownIcon ? undefined : (
                    <InputAdornment
                      position="end"
                      classes={{ root: classes.ClearButton }}
                    >
                      <img
                        src={isOpen ? chevronUpIcon : chevronDownIcon}
                        alt="icon"
                      />
                    </InputAdornment>
                  ),
                }}
                {...inputProps}
              />
            )}
          </InputMask>
        );
    }
  }

  render() {
    const {
      classes,
      renderType,
      style,
      maxDate,
      minDate,
      disableFuture,
      className,
      modalClass,
      modalStyle,
      isCustom,
    } = this.props;
    const { isOpen, dateString } = this.state;
    return (
      <ClickAwayListener onClickAway={this.onClickAway}>
        <div
          className={classnames([classes.root, className])}
          style={isCustom ? style : {}}
        >
          {this.renderTextField(renderType)}
          {isOpen && (
            <div
              className={classnames(classes.Modal, modalClass)}
              style={modalStyle}
            >
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <MUIDatePicker
                  disableFuture={disableFuture}
                  variant="static"
                  inputVariant="outlined"
                  value={dateString || moment().format('YYYY-MM-DD')}
                  format="YYYY-MM-DD"
                  maxDate={maxDate || getMaxDate()}
                  minDate={minDate || new Date('1900-01-01')}
                  onChange={this.onChangeDate}
                  onAccept={this.onAccept}
                  onClose={this.onClose}
                  onYearChange={this.onYearChange}
                />
              </MuiPickersUtilsProvider>
            </div>
          )}
        </div>
      </ClickAwayListener>
    );
  }
}

DatePicker.defaultProps = {
  modalClass: undefined,
  modalStyle: {},
};

DatePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  modalClass: PropTypes.string,
  modalStyle: PropTypes.object,
};

export default withStyles(useStyles)(DatePicker);
